import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserProfileDialog from '../User/UserProfileDialog';

interface UserProfileDrawerProps {
  open: boolean;
  onClose: () => void;
  onOpenProfileDialog?: () => void;
}

const UserProfileDrawer: React.FC<UserProfileDrawerProps> = ({ open, onClose, onOpenProfileDialog }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };
  
  const handleOpenProfileDialog = () => {
    if (onOpenProfileDialog) {
      onOpenProfileDialog();
    }
    onClose();
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  // Get user initials for avatar
  const getInitials = () => {
    if (!user) return '?';
    return `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  if (!user) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
    >
      <Box sx={{ width: 300, p: 0 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2,
          bgcolor: 'primary.main',
          color: 'white'
        }}>
          <Typography variant="h6">Profile</Typography>
          <IconButton color="inherit" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          p: 3,
          bgcolor: 'primary.light',
          color: 'white'
        }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              bgcolor: 'primary.dark',
              mb: 2,
              fontSize: '2rem'
            }}
          >
            {getInitials()}
          </Avatar>
          <Typography variant="h6">
            {user.firstName} {user.lastName}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {user.email}
          </Typography>
          <Typography variant="caption" sx={{ mt: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
            {user.role}
          </Typography>
        </Box>
        
        <List>
          <ListItem button onClick={handleOpenProfileDialog}>
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="My Profile" />
          </ListItem>
          
          <ListItem button onClick={() => handleNavigation('/change-password')}>
            <ListItemIcon>
              <SecurityIcon />
            </ListItemIcon>
            <ListItemText primary="Change Password" />
          </ListItem>
          
          {user.role === 'administrator' && (
            <>
              <Divider />
              <ListItem button onClick={() => handleNavigation('/utilities/admin/lookups')}>
                <ListItemIcon>
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText primary="Admin Panel" />
              </ListItem>
            </>
          )}
          
          <Divider />
          
          <ListItem button onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        </List>
        
        <Box sx={{ p: 2, mt: 'auto' }}>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            Utilities CRM v1.0.0
          </Typography>
        </Box>
      </Box>
      
    </Drawer>
  );
};

export default UserProfileDrawer;
